// Task permission matrix (V1.2)
// Elevated = owner | admin | tester | manager
// member = can only Start/SendToQA/Unblock on own assigned tasks
// viewer = read-only
import type { TaskInterface } from "@/interfaces";
import type { Ctx } from "./helpers";
import { isViewer } from "./helpers";

const ELEVATED = ["owner", "admin", "tester", "manager"] as const;
const PRODUCTION_ROLES = ["owner", "admin", "manager"] as const;

const isElevated = (ctx: Ctx): boolean => (ELEVATED as readonly string[]).includes(ctx.role);
const canGoToProduction = (ctx: Ctx): boolean => (PRODUCTION_ROLES as readonly string[]).includes(ctx.role);

const isAssignee = (task: TaskInterface | null | undefined, ctx: Ctx): boolean =>
    !!task?.assignee?.id && task.assignee.id === ctx.userId;

export const tasksPolicy = {
    view:              (_: Ctx) => true,

    // create/edit/delete: elevated roles only
    create:            (ctx: Ctx) => isElevated(ctx),
    editAny:           (ctx: Ctx) => isElevated(ctx),
    edit:              (ctx: Ctx) => isElevated(ctx),
    delete:            (ctx: Ctx) => isElevated(ctx),

    // ready → in_progress: member on own task only
    startTask:         (ctx: Ctx, task: TaskInterface) => !isViewer(ctx) && !isElevated(ctx) && isAssignee(task, ctx),

    // in_progress → qa: assignee OR elevated roles
    sendToQA:          (ctx: Ctx, task: TaskInterface) => !isViewer(ctx) && isAssignee(task, ctx),

    // qa → done | blocked: elevated only
    markDone:          (ctx: Ctx) => isElevated(ctx),
    rejectQA:          (ctx: Ctx) => isElevated(ctx),  // qa → qa_blocked
    resumeFromQABlocked: (ctx: Ctx) => isElevated(ctx), // qa_blocked → in_progress
    blockTask:         (ctx: Ctx) => isElevated(ctx),

    // done → production: owner/admin/manager only (no tester)
    toProduction:      (ctx: Ctx) => canGoToProduction(ctx),

    // blocked → ready: member on own task only
    unblockOwn:        (ctx: Ctx, task: TaskInterface) => !isViewer(ctx) && !isElevated(ctx) && isAssignee(task, ctx),

    // elevated can also unblock (admin/tester/manager/owner)
    unblock:           (ctx: Ctx) => isElevated(ctx),

    // acceptance criteria — define: elevated; toggle: elevated or own assignee
    defineCriteria:    (ctx: Ctx) => isElevated(ctx),
    toggleCriteria:    (ctx: Ctx, task: TaskInterface) => isElevated(ctx) || isAssignee(task, ctx),

    manageAttachment:  (ctx: Ctx, task: TaskInterface) => isElevated(ctx) || isAssignee(task, ctx),
    logTime:           (ctx: Ctx, task: TaskInterface) => isElevated(ctx) || isAssignee(task, ctx),

    // markComplete (signal done, ask for review): elevated or own assignee
    markComplete:      (ctx: Ctx, task: TaskInterface) => isElevated(ctx) || isAssignee(task, ctx),

    // legacy aliases kept so existing callers don't break
    movePhase:         (ctx: Ctx, task: TaskInterface) => isElevated(ctx) || isAssignee(task, ctx),
    finishPhase:       (ctx: Ctx, task: TaskInterface) => isElevated(ctx) || isAssignee(task, ctx),
    toggleRequirement: (ctx: Ctx, task: TaskInterface) => isElevated(ctx) || isAssignee(task, ctx),
};
